import { useCallback } from 'react';
import { getIn } from 'formik';

import { FDateTimePicker, formatDateTimePickerValue } from '../../../../components/baseV2';
import { toMonthStart } from '../../utils';
import FormFieldReadOnlyValue from '../FormFieldReadOnlyValue';
import FormFieldTopLabel from '../FormFieldTopLabel';
import { useFormReadOnly } from '../formReadOnlyProvider';
import { resolveFormDateFieldPickerStyle } from './utils';

import type { CSSProperties } from 'react';
import type { FormikValues } from 'formik';
import type { Moment } from 'moment';
import type { FormDateFieldProps } from '../../types';

export default function FormDateField<TValues extends FormikValues>({
  formik,
  name,
  label,
  placeholder,
  required = false,
  allowClear = false,
  disabled = false,
  readOnly = false,
  showFloatingLabel = true,
  showLabelAbove = false,
  showLabelBelow = false,
  showRequiredIndicator,
  sx,
  pickerStyle,
  picker,
  format,
  disabledDate,
  backgroundColor,
  onBlur,
  onChange,
}: FormDateFieldProps<TValues>) {
  const readOnlyFromContext = useFormReadOnly();
  const isReadOnly = readOnly || readOnlyFromContext;
  const fieldError = getIn(formik.errors, name);
  const isTouched = Boolean(getIn(formik.touched, name));
  const errorMessage = isTouched ? fieldError?.toString() : undefined;
  // Suppress the redundant "required field" message for empty required fields; the field still
  // highlights via `error` and its required indicator. Other validation messages still show.
  const helperText = required && !getIn(formik.values, name) ? undefined : errorMessage;

  const handleChange = useCallback(
    (value: Moment | null) => {
      const nextValue = picker === 'month' ? toMonthStart(value) : value;
      formik.setFieldValue(name, nextValue);
      onChange?.(name, nextValue);
    },
    [formik, name, onChange, picker],
  );

  const handleBlur = useCallback(() => {
    formik.setFieldTouched(name, true);
    onBlur?.(name);
  }, [formik, name, onBlur]);

  if (isReadOnly) {
    return (
      <FormFieldReadOnlyValue
        label={label}
        required={required}
        value={formatDateTimePickerValue(getIn(formik.values, name), format, true, picker)}
      />
    );
  }

  // A custom `pickerStyle` (e.g. the Agreement underline picker) keeps the legacy fill styling;
  // everything else uses the notched border so the label/Required caption sit in border gaps with
  // no fill — matching FormTextField, and looking right on colored surfaces.
  const useBorderCutout = pickerStyle == null;
  const inputLabelProps =
    useBorderCutout || backgroundColor == null ? undefined : { sx: { bgcolor: backgroundColor } };
  const requiredIndicatorSx =
    useBorderCutout || backgroundColor == null ? undefined : { bgcolor: backgroundColor };
  // In cutout mode the picker is transparent (handled in the base) so it matches the text field on
  // any surface; only the legacy custom-pickerStyle path still fills with the surface color.
  let resolvedPickerStyle: CSSProperties | undefined;
  if (!useBorderCutout) {
    resolvedPickerStyle = resolveFormDateFieldPickerStyle({
      pickerStyle: backgroundColor == null ? pickerStyle : { backgroundColor, ...pickerStyle },
      hasError: Boolean(errorMessage),
      disabled,
    });
  }

  const hasExternalLabel = showLabelAbove || showLabelBelow;

  return (
    <>
      {showLabelAbove ? <FormFieldTopLabel htmlFor={name} label={label} /> : null}
      <FDateTimePicker
        dateOnly
        disabled={disabled}
        disabledDate={disabledDate}
        allowClear={allowClear}
        borderCutout={useBorderCutout}
        error={Boolean(errorMessage)}
        format={format}
        fullWidth
        helperText={helperText}
        id={hasExternalLabel ? name : undefined}
        inputLabelProps={inputLabelProps}
        label={showFloatingLabel ? label : undefined}
        onBlur={handleBlur}
        onChange={handleChange}
        picker={picker}
        pickerStyle={resolvedPickerStyle}
        requiredIndicatorSx={requiredIndicatorSx}
        placeholder={showFloatingLabel || hasExternalLabel ? placeholder : (placeholder ?? label)}
        required={required}
        showRequiredIndicator={showRequiredIndicator}
        sx={sx}
        value={
          picker === 'month'
            ? toMonthStart(getIn(formik.values, name))
            : (getIn(formik.values, name) ?? null)
        }
      />
      {showLabelBelow ? (
        <FormFieldTopLabel htmlFor={name} label={label} placement="below" sx={{ mt: 0 }} />
      ) : null}
    </>
  );
}
